import { Primitive } from "src/types/primitive";
import { SelectField, SelectFieldProps } from "src/components/common/inputFields/SelectField";
import { ConditionalNullable, SimpleSelectFieldOption } from "../../inputFields/types";
import { FieldPath, FieldPathValue, useController, UseControllerProps } from "react-hook-form";

export interface FormSelectFieldProps<
	TFieldValues extends object,
	TFieldName extends FieldPath<TFieldValues>,
	Key extends Primitive,
	DisableClearable extends boolean | undefined,
> extends Omit<
		SelectFieldProps<SimpleSelectFieldOption<Key>, Key, DisableClearable>,
		"value" | "getOptionKey" | "getOptionLabel"
	> {
	control: UseControllerProps<TFieldValues, TFieldName>["control"];
	name: UseControllerProps<TFieldValues, TFieldName>["name"];
	rules?: UseControllerProps<TFieldValues, TFieldName>["rules"];
}

export const FormSelectField = <
	TFieldValues extends object,
	TFieldName extends FieldPath<TFieldValues>,
	Key extends Primitive,
	DisableClearable extends boolean | undefined,
>({
	  control,
	  name,
	  rules,
	  onChange,
	  ...other
  }: FormSelectFieldProps<TFieldValues, TFieldName, Key, DisableClearable>) => {
	const { field, fieldState } = useController({
		name,
		control,
		rules,
		defaultValue: "" as FieldPathValue<TFieldValues, TFieldName>
	});

	const transformedField = {
		...field,
		onChange: (value: Key | null) => {
			field.onChange(value);
			onChange?.(value as ConditionalNullable<Key, DisableClearable>);
		},
		ref: undefined
	};

	return (
		<SelectField
			getOptionKey={<Key extends Primitive>(
				o: SimpleSelectFieldOption<Key>,
			): Key => {
				return o.key;
			}}
			getOptionLabel={<Key extends Primitive>(
				o: SimpleSelectFieldOption<Key>,
			): string => {
				return o.label;
			}}
			error={fieldState.error?.message}
			{...transformedField}
			{...other}
		/>
	);
};
